import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Address } from "@models/address/address";
import { EntityType } from '@models/common/entity-type.enum';
import { Company } from "@models/companies/company";
import { CompanyRecord } from "@models/companies/company-record";
import { Document } from "@models/documents/document";
import { DocumentationType, } from "@models/documents/document-enums";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { IndividualData } from "@models/individuals/individual-data";
import { ParsedName } from "@models/individuals/parsed-name";
import { OfficerRelationshipDetails, Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { SelectOption } from "@models/ui-models/select-options";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import {
  DinMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/din-masked-input/din-masked-input.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { CountriesService } from "@shared/services/countries.service";
import { parseFullName } from "@shared/utils/parse-fullname";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { directorFullNameValidatorMsg } from "@shared/validators/custom-form-validators-messages";
import { DatePickerFormValidators, DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { forkJoin } from "rxjs";
import { CompanyChangeOfficer } from "../../../models/company";
import { poBoxAddressCustomErrors } from '../../../models/company/company-change-address.model';
import {
  IndividualDataFormGroup,
  IndividualDataFormGroupComponent
} from '../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component';
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import {
  getPositionTypesOptions
} from '../../company/company-change-officer-appoint/company-change-officer-appoint.component';
import {
  BaseBulkStepperFormComponent,
  StepperFormComponent,
  StepperFormDescriptionComponent
} from "../../stepper-form";
import { IBulkAppliedChange } from "../base-bulk-stepper-form/base-bulk-stepper-form.component";
import { BulkCompanyChangeOfficerAppointStepsEnum } from "./bulk-company-change-officer-appoint-steps.enum";

interface OfficerAppointmentControls {
  changeDate: FormControl<Date | null>;
  types: FormControl<RelationshipType[]>;
  din: FormControl<string | null>;
  documentationType: FormControl<DocumentationType | null>;
  newOfficeholder: IndividualDataFormGroup;
}

@Component({
  selector: 'app-bulk-appointment-officeholder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    DatePickerComponent,
    DividerComponent,
    IndividualDataFormGroupComponent,
    ValidationErrorComponent,
    CheckboxGroupComponent,
    DinMaskedInputComponent,
    RadioComponent,
    DisclaimerComponent,
    ListGridClientSideComponent,
  ],
  providers: [],
  templateUrl: 'bulk-company-change-officer-appoint.component.html',
  styleUrls: ['../../stepper-form/base-stepper-component/base-stepper-form.component.scss'],
})
export class BulkCompanyChangeOfficerAppointComponent extends BaseBulkStepperFormComponent<BulkCompanyChangeOfficerAppointStepsEnum, CompanyChangeOfficer> implements OnInit {
  @Input() officers: Record<string, Relationship[]> = {};
  @Input() officersSelectOptions: SelectOption[] = [];
  @Input() allDirectorsOptions: SelectOption[] = [];
  @Input() useInternationalAddresses = false;
  @Input() formerNameVisible = false;
  @Input() companyRecords: CompanyRecord[] = [];

  override readonly StepsEnum = BulkCompanyChangeOfficerAppointStepsEnum;
  readonly generateDocumentsInfo = 'If you need to create a Resolution or Minutes, you must open and edit the forms individually.';
  readonly customDatepickerErrors = DatePickerFormValidators.errorMessagesWithDateOfEstablishmentErrorMessages;
  readonly directorFullNameValidatorMsg = directorFullNameValidatorMsg;
  readonly entityType = EntityType.Individual;
  readonly individualDataFormLabels = {
    fullName: 'Full Name',
    formerName: 'Former Name (if any)',
    dob: 'Date of Birth',
    birthCity: 'City of Birth',
    birthCountry: 'Country of Birth',
    address: 'Address',
  };
  readonly minDate = DatepickerHelper.getDefaultMinDateStruct();
  readonly maxDate = DatepickerHelper.getTodayStruct();
  readonly addressesCustomErrors = poBoxAddressCustomErrors;

  positionTypesOptions = getPositionTypesOptions();

  form = new FormGroup<OfficerAppointmentControls>({
    changeDate: new FormControl<Date | null>(DatepickerHelper.getToday()),
    types: new FormControl<RelationshipType[]>([], { nonNullable: true }),
    din: new FormControl<string | null>(null),
    documentationType: new FormControl<DocumentationType>(DocumentationType.FormOnly, [Validators.required]),
    newOfficeholder: IndividualDataFormGroupComponent.defineForm()
  });

  override stepperForm = new FormGroup({
    [BulkCompanyChangeOfficerAppointStepsEnum.FormDescription]: new FormGroup({}),
    [BulkCompanyChangeOfficerAppointStepsEnum.AppointOfficer]: this.form,
    [BulkCompanyChangeOfficerAppointStepsEnum.Summary]: this.form,
  });

  constructor() {
    super();
    this.setupSteps(BulkCompanyChangeOfficerAppointStepsEnum);
  }

  ngOnInit(): void {
    this.setupChange();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);
    CheckboxGroupComponent.addRequiredAtLeastOneValidator(this.form.controls.types);
    this.loadCompanies();
  }

  loadCompanies(): void {
    this.isLoading.set(true);

    const selectedEntitiesLoadRequests = this.companyRecords
      .map((companyRecord) => this.loadCompany(companyRecord.entityId));

    forkJoin(selectedEntitiesLoadRequests)
      .subscribe({
        next: (fullCompanies) => {
          this.companies = fullCompanies.filter(Boolean) as Company[];
          this.fillRows(this.companies);
          this.isLoading.set(false);
        }
      });
  }

  onStepChange(stepIndex: BulkCompanyChangeOfficerAppointStepsEnum): void {
    if (stepIndex === BulkCompanyChangeOfficerAppointStepsEnum.Summary) {
      this.fillRowsErrors();
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeOfficer);
  }

  override setupChange(change: CompanyChangeOfficer = this.formModel): void {
  }

  override buildDocument(): Document[] | null {
    if (this.areAllRowsHaveErrors) return null;

    const documentationType = this.form.controls.documentationType.value!;
    const formValue = this.form.value;
    const names = parseFullName(formValue.newOfficeholder?.fullName ?? '');
    const formerName = parseFullName(formValue.newOfficeholder?.formerFullName ?? '') as ParsedName;
    const changeDate = this.form.controls.changeDate.value!;
    const types = this.form.controls.types.value;
    const change = new CompanyChangeOfficer({ changeDate });
    const birthCountry = CountriesService.getAustralianCodeByStateName(formValue.newOfficeholder?.birthCountry ?? '')
      || (formValue.newOfficeholder?.birthCountry ?? '');

    const individualDataOverride = new IndividualData({
      ...names,
      formerName,
      birthCountry,
      dob: formValue.newOfficeholder?.dob ?? undefined,
      address: new Address({
        ...formValue.newOfficeholder?.address,
        normalizedFullAddress: formValue.newOfficeholder?.address?.normalizedFullAddress,
      } as Address),
      birthCity: formValue.newOfficeholder?.birthCity ?? '',
    });

    return this.companies.map((company, index) => {
      const relatedRow = this.rows[index];
      const changeAuthorisation = this.getChangeAuthorisation(company, documentationType, relatedRow, changeDate);
      changeAuthorisation.documentationType = documentationType;

      change.offices = types.map((type) => {
        const relationship = new Relationship({
          type,
          individualDataOverride,
          start: changeDate,
          entityId: company.entityId,
          organisationId: changeAuthorisation.authorisingParty.organisationId,
        });

        if (type == RelationshipType.AlternativeDirector) {
          relationship.details = new OfficerRelationshipDetails();
        }

        if ([RelationshipType.Director, RelationshipType.AlternativeDirector].includes(type) && relationship.individualDataOverride) {
          relationship.individualDataOverride.din = formValue.din ?? '';
        }

        return relationship;
      });

      return new Document({
        changes: [change],
        changeAuthorisation,
        entityId: company.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
        documentationType: changeAuthorisation.documentationType
      });
    });
  }

  override updateDocumentationTypeOptions(): void {
    this.documentTypeOptions = this.documentTypeOptions.map((option) => ({
      ...option,
      disabled: option.value !== DocumentationType.FormOnly
    }));
    const firstEnabledOption = this.documentTypeOptions.find((option) => !option.disabled)?.value ?? null;
    this.form.controls.documentationType.setValue(firstEnabledOption as DocumentationType);
  }

  onOfficerTypesChanges(values: unknown[]): void {
    const types = (values ?? []) as RelationshipType[];
    const typesIncludeDirector = types.includes(RelationshipType.Director);
    const typesIncludeAltDirector = types.includes(RelationshipType.AlternativeDirector);

    setControlDisabled(this.form.controls.din, !!types.length && !(typesIncludeDirector || typesIncludeAltDirector));

    this.form.updateValueAndValidity();
  }

  onIndividualSelected($event: IndividualData) {
    this.form.controls.din.patchValue($event.din);
  }

  get isDirectorTypeSelected(): boolean {
    return this.form.controls.types.value.includes(RelationshipType.Director)
      || this.form.controls.types.value.includes(RelationshipType.AlternativeDirector);
  }

  get fullNameForDinCheck(): string | null {
    if (this.form.controls.newOfficeholder.controls.fullName.valid && this.form.controls.newOfficeholder.controls.fullName.value) {
      const parsedName = parseFullName(this.form.controls.newOfficeholder.controls.fullName.value);
      return [parsedName.firstName, parsedName.lastName].join(' ').trim();
    }

    return null;
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  override getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    const company = this.companies[rowIndex];

    const relatedChangeAddressEntityEstablishmentDate = company.dateOfEstablishment
      ? new Date(company.dateOfEstablishment)
      : company.dateOfEstablishment;
    const changeDate = this.form.controls.changeDate.value;

    if (relatedChangeAddressEntityEstablishmentDate && changeDate && changeDate <= relatedChangeAddressEntityEstablishmentDate) {
      return 'Date of change cannot be earlier or same as the company registration date';
    }

    return super.getRowError(row, rowIndex);
  }
}
